import firebase from "firebase/compat/app";
import { observable } from "mobx";
import AccountService from "./AccountService";

export type ChatContact = {
  contactId: string;
  name: string;
  phone: string;
  email: string;
  date: string;
  lastMessage: string;
};

export type ChatRecipientMessage = {
  mId: string;
  content: string;
  status?: "sent" | "opened" | "clicked" | "reviewed";
  type: "customer" | "user";
};

export type ChatStatus = "INVALID_USER" | "SUCCESS" | "ERROR";

export default class ChatMessaengerService {
  private _accountService: AccountService;
  private _db: firebase.firestore.Firestore = firebase.firestore();
  private _funcs: firebase.functions.Functions = firebase.functions();

  constructor(account: AccountService) {
    this._accountService = account;
  }

  async initialize() {
    this._db = firebase.firestore();
  }

  @observable
  private _selectedChatContact: ChatContact | any = null;

  @observable
  private _chatContacts: Array<ChatContact | any> = [];

  @observable
  private _chatMessages: Array<any> = [];

  public get chatContacts(): Array<ChatContact> {
    return this._chatContacts;
  }

  public get selectedChatContact(): ChatContact {
    return this._selectedChatContact;
  }

  public setSelectedChatContact(contact: any) {
    this._selectedChatContact = contact;
  }

  public get chatMessages(): any {
    return this._chatMessages;
  }

  async getChatContacts(): Promise<ChatStatus> {
    const user = this._accountService.user;
    if (user == null) {
      return "INVALID_USER";
    }
    try {
      const result = await this._db
        .collection("chatContacts")
        .orderBy("date", "desc")
        .get();
      const chatContacts = result.docs.map((doc) => doc.data());
      if (chatContacts.length > 0) {
        this._chatContacts = chatContacts;
        this._selectedChatContact = chatContacts[0];
      }
      return "SUCCESS";
    } catch (err) {
      console.log({ err });
      return "ERROR";
    }
  }

  async saveChatContact(
    contactId: string,
    content: string,
    data: any
  ): Promise<ChatStatus> {
    const { name, phone, email, mId } = data;
    try {
      await this._db.collection("chatContacts").doc(contactId).set({
        contactId,
        mId,
        name,
        phone,
        email,
        lastMessage: content,
        date: new Date().toISOString(),
      });
      return "SUCCESS";
    } catch (err) {
      console.log({ err });
      return "ERROR";
    }
  }

  async saveSentMessages(
    userId: string,
    contactId: string,
    data: ChatRecipientMessage
  ): Promise<ChatStatus> {
    const { mId, content, type } = data;
    try {
      const result = await this._db
        .collection("chatMessenger")
        .doc(userId)
        .collection("contacts")
        .doc(contactId)
        .collection("messages")
        .doc(mId)
        .set({
          mId,
          content,
          status: "sent",
          type,
          date: Date.now(),
        });
      return "SUCCESS";
    } catch (err) {
      console.log({ err });
      return "ERROR";
    }
  }

  async updateMessageStatus(
    userId: string,
    contactId: string,
    mId: string
  ): Promise<ChatStatus> {
    try {
      const result = await this._db
        .collection("chatMessenger")
        .doc(userId)
        .collection("contacts")
        .doc(contactId)
        .collection("messages")
        .doc(mId)
        .set(
          {
            status: "opened",
          },
          { merge: true }
        );
      return "SUCCESS";
    } catch (err) {
      console.log({ err });
      return "ERROR";
    }
  }

  async getChatMessages(contactId: string): Promise<ChatStatus> {
    const userId = this._accountService.user.uid;
    if (!userId) {
      return "INVALID_USER";
    }
    try {
      const results = await this._db
        .collection("chatMessenger")
        .doc(userId)
        .collection("contacts")
        .doc(contactId)
        .collection("messages")
        .orderBy("date", "asc")
        .get();
      const messages = results.docs.map((doc) => doc.data());
      this._chatMessages = messages;
      return "SUCCESS";
    } catch (err) {
      console.log({ err });
      return "ERROR";
    }
  }
}
